import React, { useState, useEffect, useMemo } from 'react';
import skillManagementService from '../../services/skillManagementService';
import { validateSkillContent, getFlagColor, getFlagBadge } from '../../utils/skillValidation';
import { Search, Filter, Trash2, AlertTriangle, CheckCircle, X, RefreshCw, BarChart3, Users, Flag, Check } from 'lucide-react';

const SkillManagement = ({ onPendingCountChange }) => {
  const [skills, setSkills] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'stats'
  const [currentPage, setCurrentPage] = useState(1);
  const [flaggedSkills, setFlaggedSkills] = useState([]);
  const SKILLS_PER_PAGE = 20;

  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      console.log('[SkillManagement] Loading data with filters:', { searchTerm, categoryFilter, roleFilter });
      
      const [skillsResponse, categoriesResponse, statsResponse] = await Promise.all([
        skillManagementService.getAllUserSkills({ 
          searchTerm, 
          category: categoryFilter,
          userRole: roleFilter
        }),
        skillManagementService.getSkillCategories(),
        skillManagementService.getSkillsStatistics()
      ]);

      console.log('[SkillManagement] Skills response:', skillsResponse);
      console.log('[SkillManagement] Categories response:', categoriesResponse);
      console.log('[SkillManagement] Stats response:', statsResponse);

      if (skillsResponse.success) {
        console.log('[SkillManagement] Setting skills:', skillsResponse.data?.length, 'items');
        
        // Validate skills for inappropriate content and separate flagged ones
        const validated = (skillsResponse.data || []).map(skill => ({
          ...skill,
          flagValidation: validateSkillContent(skill.skillName)
        }));
        
        // Separate flagged and approved skills
        const flagged = validated.filter(s => s.flagValidation.isFlagged);
        const approved = validated.filter(s => !s.flagValidation.isFlagged);
        
        // Keep flagged at top for visibility
        setFlaggedSkills(flagged);
        setSkills([...flagged, ...approved]);
      } else {
        console.warn('[SkillManagement] Skills response not successful:', skillsResponse);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }

      if (statsResponse.success) {
        setStatistics(statsResponse.data);
      }
    } catch (error) {
      showNotification('Failed to load skills data', 'error');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadData();
  }, [categoryFilter, roleFilter]);

  // Report pending count to parent
  useEffect(() => {
    if (onPendingCountChange) {
      console.log('[SkillManagement] Reporting flagged count:', flaggedSkills.length);
      onPendingCountChange(flaggedSkills.length);
    }
  }, [flaggedSkills.length, onPendingCountChange]);

  // Filter skills based on search term
  const filteredSkills = useMemo(() => {
    // Ensure skills is always an array
    const skillsArray = Array.isArray(skills) ? skills : [];
    
    if (!searchTerm) return skillsArray;
    
    const searchLower = searchTerm.toLowerCase();
    return skillsArray.filter(skill =>
      skill.skillName?.toLowerCase().includes(searchLower) ||
      skill.skillCategory?.toLowerCase().includes(searchLower) ||
      skill.userName?.toLowerCase().includes(searchLower) ||
      skill.userEmail?.toLowerCase().includes(searchLower) ||
      skill.skillExperience?.toLowerCase().includes(searchLower)
    );
  }, [skills, searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSkills.length / SKILLS_PER_PAGE);
  const paginatedSkills = useMemo(() => {
    const startIndex = (currentPage - 1) * SKILLS_PER_PAGE;
    const endIndex = startIndex + SKILLS_PER_PAGE;
    return filteredSkills.slice(startIndex, endIndex);
  }, [filteredSkills, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, roleFilter]);

  // Handle search with debounce
  const handleSearch = () => {
    loadData();
  };

  // Handle skill deletion
  const handleDeleteSkill = async (skill) => {
    setSkillToDelete(skill);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!skillToDelete) return;

    setActionLoading(true);
    try {
      // Use the new deleteUserSkillByName method which handles comma-separated skills correctly
      const response = await skillManagementService.deleteUserSkillByName(
        skillToDelete.userId,
        skillToDelete.skillName
      );

      console.log('[SkillManagement] Delete response:', response);

      if (response.success) {
        const message = response.data?.message || 'Skill deleted successfully';
        showNotification(message, 'success');
        setShowDeleteModal(false);
        setSkillToDelete(null);
        loadData(); // Reload data
      } else {
        showNotification(`Failed to delete skill: ${response.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showNotification(`Failed to delete skill: ${error.message}`, 'error');
      console.error('Error deleting skill:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk delete by skill name
  const handleBulkDelete = async (skillName) => {
    if (!window.confirm(`Are you sure you want to delete ALL instances of "${skillName}" across all users? This action cannot be undone.`)) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await skillManagementService.bulkDeleteSkillByName(skillName);

      console.log('[SkillManagement] Bulk delete response:', response);

      if (response.success) {
        const message = response.data?.message || `Successfully deleted "${skillName}"`;
        showNotification(message, 'success');
        loadData(); // Reload data
      } else {
        showNotification(`Failed to bulk delete skill: ${response.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showNotification(`Failed to bulk delete skill: ${error.message}`, 'error');
      console.error('Error bulk deleting skill:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle approving a flagged skill (admin override)
  const handleApproveFlaggedSkill = async (skill) => {
    setActionLoading(true);
    try {
      // For now, just remove from flagged by re-validating
      // In a full implementation, this would update the database
      const updatedSkills = skills.map(s => 
        s.userId === skill.userId && s.skillName === skill.skillName
          ? { ...s, flagValidation: { isFlagged: false, reason: null } }
          : s
      );
      
      setSkills(updatedSkills);
      setFlaggedSkills(updatedSkills.filter(s => s.flagValidation.isFlagged));
      showNotification(`Approved skill "${skill.skillName}"`, 'success');
    } catch (error) {
      showNotification(`Failed to approve skill: ${error.message}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Group skills by name for easier management
  const groupedSkills = useMemo(() => {
    const groups = {};
    const skillsArray = Array.isArray(filteredSkills) ? filteredSkills : [];
    
    skillsArray.forEach(skill => {
      if (!groups[skill.skillName]) {
        groups[skill.skillName] = [];
      }
      groups[skill.skillName].push(skill);
    });
    return groups;
  }, [filteredSkills]);

  // Get color for category badge
  const getCategoryColor = (category) => {
    const colors = {
      'Administrative': 'bg-blue-100 text-blue-800',
      'Technical': 'bg-green-100 text-green-800',
      'Creative': 'bg-purple-100 text-purple-800',
      'Communication': 'bg-yellow-100 text-yellow-800',
      'Marketing': 'bg-pink-100 text-pink-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['Other'];
  };

  // Render statistics view
  const renderStatistics = () => {
    if (!statistics) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{statistics?.totalUsers || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Skills</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{statistics?.totalSkills || 0}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Custom Skills</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{statistics?.customSkills || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Skills/User</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {(statistics?.totalUsers || 0) > 0 ? ((statistics?.totalSkills || 0) / (statistics?.totalUsers || 1)).toFixed(1) : 0}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Top Skills */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Skills</h3>
          <div className="space-y-2">
            {Array.isArray(statistics?.topSkillsList) && statistics.topSkillsList.length > 0 ? (
              statistics.topSkillsList.map((skill, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-900">{skill?.name || 'Unknown'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{skill?.count || 0} users</span>
                    <button
                      onClick={() => handleBulkDelete(skill?.name)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete all instances"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No top skills data available</p>
            )}
          </div>
        </div>

        {/* Skills by Category */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {statistics?.skillsByCategory && Object.entries(statistics.skillsByCategory).map(([category, count]) => (
              <div key={category} className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-600">{category}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{count}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Skills by Role */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills by User Role</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-gray-600">VA Skills</p>
              <p className="text-xl font-bold text-blue-600 mt-1">{statistics?.skillsByRole?.va || 0}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-gray-600">Employer Skills</p>
              <p className="text-xl font-bold text-green-600 mt-1">{statistics?.skillsByRole?.employer || 0}</p>
            </div>
            <div className="p-4 bg-gray-100 rounded-lg">
              <p className="text-sm font-medium text-gray-600">Other Skills</p>
              <p className="text-xl font-bold text-gray-700 mt-1">{statistics?.skillsByRole?.other || 0}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-md p-4 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 
          notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
        } text-white`}>
          <div className="flex items-center justify-between">
            <p>{notification.message}</p>
            <button onClick={() => setNotification(null)} className="ml-4">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Skills Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage skills added by users through the up-skills page
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'stats' : 'list')}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {viewMode === 'list' ? 'View Statistics' : 'View List'}
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Flagged Skills Alert */}
      {flaggedSkills.length > 0 && viewMode === 'list' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <Flag className="text-red-600 flex-shrink-0 mt-1" size={20} />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900">
                {flaggedSkills.length} Skill{flaggedSkills.length !== 1 ? 's' : ''} Pending Moderation
              </h3>
              <p className="text-xs text-red-700 mt-1">
                The following skills contain inappropriate content or may not be valid. Review and approve or delete them.
              </p>
            </div>
          </div>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {flaggedSkills.slice(0, 5).map((skill, idx) => (
              <div key={`${skill.userId}-${skill.skillIndex}-flagged-${idx}`} className={`p-3 rounded-lg border ${getFlagColor(skill.flagValidation.reason)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{skill.skillName}</span>
                      <span className={`text-xs px-2 py-1 rounded-full bg-opacity-20 border ${getFlagColor(skill.flagValidation.reason)}`}>
                        {getFlagBadge(skill.flagValidation.reason)}
                      </span>
                    </div>
                    <p className="text-xs opacity-75 mt-1">
                      {skill.userName} ({skill.userRole.toUpperCase()}) - {skill.flagValidation.message}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <button
                      onClick={() => handleApproveFlaggedSkill(skill)}
                      className="p-2 hover:bg-green-100 rounded text-green-600"
                      title="Approve skill"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteSkill(skill)}
                      className="p-2 hover:bg-red-100 rounded text-red-600"
                      title="Delete skill"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {flaggedSkills.length > 5 && (
              <p className="text-xs text-center text-red-600 pt-2">
                +{flaggedSkills.length - 5} more flagged skill{flaggedSkills.length - 5 !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}

      {viewMode === 'stats' ? (
        renderStatistics()
      ) : (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search skills, users, or categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {Array.isArray(categories) && categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Role Filter */}
              <div>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Roles</option>
                  <option value="va">Virtual Assistants</option>
                  <option value="employer">Employers</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Count and Pagination Info */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {paginatedSkills.length > 0 ? (currentPage - 1) * SKILLS_PER_PAGE + 1 : 0}-{Math.min(currentPage * SKILLS_PER_PAGE, filteredSkills.length)} of {filteredSkills.length} skill{filteredSkills.length !== 1 ? 's' : ''} (Page {currentPage} of {totalPages || 1})
            </p>
          </div>

          {/* Skills List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-primary" size={32} />
            </div>
          ) : filteredSkills.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <AlertTriangle className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">No skills found matching your filters.</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Skill Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Experience
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedSkills.map((skill, index) => (
                    <tr key={`${skill.userId}-${skill.skillIndex}-${skill.skillName}`} className={`hover:bg-gray-50 ${skill.flagValidation?.isFlagged ? 'bg-red-50 border-l-4 border-l-red-500' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{skill.skillName}</div>
                            {skill.skillType === 'upskills' && (
                              <span className="text-xs text-purple-600">Custom</span>
                            )}
                          </div>
                          {skill.flagValidation?.isFlagged && (
                            <span className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ${getFlagColor(skill.flagValidation.reason)}`}>
                              {getFlagBadge(skill.flagValidation.reason)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(skill.skillCategory)}`}>
                          {skill.skillCategory}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{skill.userName}</div>
                        <div className="text-xs text-gray-500">{skill.userEmail}</div>
                        <span className={`text-xs ${skill.userRole === 'va' ? 'text-blue-600' : 'text-green-600'}`}>
                          {skill.userRole.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {skill.skillRating ? (
                          <div className="flex items-center">
                            <span className="text-sm text-yellow-500">★</span>
                            <span className="text-sm text-gray-900 ml-1">{skill.skillRating}/5</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={skill.skillExperience}>
                          {skill.skillExperience || <span className="text-gray-400">No experience added</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {skill.flagValidation?.isFlagged && (
                            <button
                              onClick={() => handleApproveFlaggedSkill(skill)}
                              className="p-2 hover:bg-green-100 rounded text-green-600"
                              title="Approve this skill"
                            >
                              <Check size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteSkill(skill)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete this skill"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          currentPage === page
                            ? 'bg-primary text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Skill</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{skillToDelete?.skillName}</strong> from <strong>{skillToDelete?.userName}'s</strong> profile? This action cannot be undone.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSkillToDelete(null);
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillManagement;
